import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet, Font } from '@react-pdf/renderer';
import type { Package, TravelAgency } from '../../types/pdf';
import { getImageUrl } from '../../utils/imageUrl';

Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf', fontWeight: 300 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf', fontWeight: 400 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf', fontWeight: 500 },
    { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 700 },
  ]
});

interface DayItinerary {
  id: number;
  dayNumber: number;
  title: string;
  description: string;
  activities: string[];
  accommodation: string;
  meals: { breakfast: boolean; lunch: boolean; dinner: boolean };
}

interface TravelAgencyPDF {
  name: string;
  logo: string;
  phone: string;
  email: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
    font: string;
    headerStyle: string;
  };
}

interface PackagePDFProps {
  package: Package;
  agency: TravelAgencyPDF;
}

const PackagePDF: React.FC<PackagePDFProps> = ({ package: pkg, agency }) => {
  const primary = agency.theme?.primaryColor || '#1E40AF';
  const secondary = agency.theme?.secondaryColor || '#1E3A5F';

  const s = StyleSheet.create({
    page: { padding: 0, backgroundColor: '#FFFFFF', fontFamily: 'Roboto' },
    contentPage: { padding: 32, paddingBottom: 60, backgroundColor: '#FFFFFF', fontFamily: 'Roboto' },
    coverPage: { position: 'relative', height: '100%', width: '100%' },
    coverImage: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'contain' },
    coverOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: 30, backgroundColor: 'rgba(0,0,0,0.55)' },
    coverTitle: { fontSize: 28, fontWeight: 700, color: '#FFFFFF', marginBottom: 6 },
    coverSub: { fontSize: 13, color: '#FFFFFF', opacity: 0.9 },
    coverPrice: { fontSize: 20, fontWeight: 700, color: '#FFFFFF', marginTop: 8 },
    coverPriceOld: { fontSize: 14, color: '#FFFFFF', opacity: 0.7, textDecoration: 'line-through', marginRight: 8 },

    header: { flexDirection: 'row', alignItems: 'center', marginBottom: 24, paddingBottom: 16, borderBottomWidth: 2, borderBottomColor: primary },
    logo: { width: 56, height: 56, objectFit: 'contain', borderRadius: 4 },
    headerText: { marginLeft: 14, flex: 1 },
    agencyName: { fontSize: 16, fontWeight: 700, color: '#1F2937' },
    agencyContact: { fontSize: 9, color: '#6B7280', marginTop: 2 },

    sectionTitle: { fontSize: 15, fontWeight: 700, color: primary, marginBottom: 10, paddingBottom: 6, borderBottomWidth: 1.5, borderBottomColor: `${primary}30` },
    section: { marginBottom: 18 },
    pageBreak: { breakBefore: 'page' as const },

    infoRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 0 },
    infoCard: { width: '33%', paddingVertical: 8, paddingHorizontal: 6 },
    infoLabel: { fontSize: 8, color: '#9CA3AF', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 2 },
    infoValue: { fontSize: 11, color: '#1F2937', fontWeight: 500 },

    text: { fontSize: 10, color: '#374151', lineHeight: 1.6 },
    textBold: { fontSize: 10, color: '#1F2937', fontWeight: 600 },

    listItem: { flexDirection: 'row', marginBottom: 4, paddingLeft: 4 },
    bullet: { width: 14, fontSize: 10, color: primary },
    bulletRed: { width: 14, fontSize: 10, color: '#EF4444' },
    listText: { fontSize: 10, color: '#374151', flex: 1 },

    formulaCard: { backgroundColor: '#F9FAFB', borderRadius: 6, padding: 14, marginBottom: 12, borderWidth: 1, borderColor: '#E5E7EB' },
    formulaName: { fontSize: 13, fontWeight: 700, color: '#1F2937', marginBottom: 6 },
    formulaPrice: { fontSize: 12, fontWeight: 600, color: primary, marginBottom: 4 },
    formulaPriceOld: { fontSize: 10, color: '#9CA3AF', textDecoration: 'line-through', marginRight: 6 },
    formulaRow: { flexDirection: 'row', alignItems: 'center' },

    roomRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: '#E5E7EB' },
    roomLabel: { fontSize: 9, color: '#6B7280' },
    roomPrice: { fontSize: 9, color: '#1F2937', fontWeight: 600 },

    hotelBlock: { marginTop: 8, paddingTop: 8, borderTopWidth: 0.5, borderTopColor: '#E5E7EB' },
    hotelName: { fontSize: 11, fontWeight: 600, color: '#1F2937' },
    hotelDetail: { fontSize: 9, color: '#6B7280', marginTop: 1 },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 },
    chip: { backgroundColor: `${primary}12`, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 3 },
    chipText: { fontSize: 8, color: primary },

    dayCard: { marginBottom: 12, padding: 12, backgroundColor: '#FAFAFA', borderRadius: 4, borderLeftWidth: 3, borderLeftColor: primary },
    dayTitle: { fontSize: 12, fontWeight: 700, color: '#1F2937', marginBottom: 4 },
    dayDesc: { fontSize: 9, color: '#4B5563', marginBottom: 6 },
    mealRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
    mealChip: { backgroundColor: '#FEF3C7', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 3 },
    mealText: { fontSize: 8, color: '#92400E' },
    accomText: { fontSize: 9, color: '#6B7280', fontStyle: 'italic' as const, marginTop: 3 },

    footer: { position: 'absolute', bottom: 16, left: 32, right: 32, flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 0.5, borderTopColor: '#E5E7EB', paddingTop: 8 },
    footerText: { fontSize: 7, color: '#9CA3AF' },
  });

  const clean = (t: string | undefined | null) => (t || '').replace(/[=R]/g, '').trim();

  const fmtDate = (ts: string | number | undefined | null) => {
    if (!ts) return null;
    const n = typeof ts === 'string' ? parseInt(ts) : ts;
    const d = new Date(n);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const fmtPrice = (v: number) => v > 0 ? new Intl.NumberFormat('fr-FR').format(Math.round(v)) : null;

  const dep = pkg.departures?.[0];
  const depDate = fmtDate(dep?.departureDate);
  const retDate = fmtDate(dep?.returnDate);
  const duration = (() => {
    if (!dep?.departureDate || !dep?.returnDate) return null;
    const a = typeof dep.departureDate === 'string' ? parseInt(dep.departureDate) : dep.departureDate;
    const b = typeof dep.returnDate === 'string' ? parseInt(dep.returnDate) : dep.returnDate;
    const days = Math.ceil(Math.abs(b - a) / 86400000);
    return days > 0 ? `${days} jours / ${days - 1} nuits` : null;
  })();

  const heroPrice = (() => {
    const f = pkg.formulas?.[0];
    if (!f) return { original: null, final: null };
    let p = 0;
    if (f.priceType === 'PER_PERSON' && f.perPersonPrice && f.perPersonPrice > 0) p = f.perPersonPrice;
    else if (f.roomPrices) {
      const prices = Object.values(f.roomPrices).filter((v: number) => v > 0);
      if (prices.length > 0) p = Math.min(...prices);
    }
    if (p <= 0) return { original: null, final: null };
    if (f.discountPercent && f.discountPercent > 0) return { original: p, final: Math.round(p * (1 - f.discountPercent / 100)) };
    return { original: null, final: p };
  })();

  const highlights = (pkg.highlights || []).filter(h => clean(h));
  const included = (pkg.included || []).filter(i => clean(i));
  const excluded = (pkg.excluded || []).filter(e => clean(e));
  const hasItinerary = pkg.itinerary && pkg.itinerary.length > 0;
  const hasImages = pkg.images && pkg.images.length > 0;
  const departure = clean(pkg.departure);

  const roomLabels: Record<string, string> = { single: 'Single (1 pers.)', double: 'Double (2 pers.)', triple: 'Triple (3 pers.)', quadruple: 'Quadruple (4 pers.)', quintuple: 'Quintuple (5 pers.)' };

  return (
    <Document>
      {/* COVER */}
      <Page size="A4" style={s.page}>
        <View style={s.coverPage}>
          <Image src={getImageUrl(pkg.image)} style={s.coverImage} />
          <View style={s.coverOverlay}>
            <Text style={s.coverTitle}>{clean(pkg.title)}</Text>
            {departure ? <Text style={s.coverSub}>Départ de {departure}</Text> : null}
            {duration ? <Text style={s.coverSub}>{duration}</Text> : null}
            {heroPrice.final ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                {heroPrice.original ? <Text style={s.coverPriceOld}>{fmtPrice(heroPrice.original)} DA</Text> : null}
                <Text style={s.coverPrice}>À partir de {fmtPrice(heroPrice.final)} DA</Text>
              </View>
            ) : null}
          </View>
        </View>
      </Page>

      {/* CONTENT */}
      <Page size="A4" style={s.contentPage}>
        {/* Header */}
        <View style={s.header}>
          <Image src={getImageUrl(agency.logo)} style={s.logo} />
          <View style={s.headerText}>
            <Text style={s.agencyName}>{clean(agency.name)}</Text>
            <Text style={s.agencyContact}>{[agency.phone, agency.email].filter(Boolean).join(' | ')}</Text>
          </View>
        </View>

        {/* Key info */}
        <View style={{ ...s.section, backgroundColor: '#F9FAFB', borderRadius: 6, padding: 10 }}>
          <View style={s.infoRow}>
            {depDate ? <View style={s.infoCard}><Text style={s.infoLabel}>Départ</Text><Text style={s.infoValue}>{depDate}</Text></View> : null}
            {retDate ? <View style={s.infoCard}><Text style={s.infoLabel}>Retour</Text><Text style={s.infoValue}>{retDate}</Text></View> : null}
            {duration ? <View style={s.infoCard}><Text style={s.infoLabel}>Durée</Text><Text style={s.infoValue}>{duration}</Text></View> : null}
            {departure ? <View style={s.infoCard}><Text style={s.infoLabel}>Départ de</Text><Text style={s.infoValue}>{departure}</Text></View> : null}
          </View>
        </View>

        {/* Description */}
        {clean(pkg.description) ? (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Description</Text>
            <Text style={s.text}>{clean(pkg.description)}</Text>
          </View>
        ) : null}

        {/* Points forts */}
        {highlights.length > 0 ? (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Points forts</Text>
            {highlights.map((h, i) => (
              <View key={i} style={s.listItem}>
                <Text style={s.bullet}>•</Text>
                <Text style={s.listText}>{clean(h)}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {/* Inclus / Non inclus côte à côte */}
        {(included.length > 0 || excluded.length > 0) ? (
          <View style={{ ...s.section, ...s.pageBreak }}>
            <View style={{ flexDirection: 'row', gap: 16 }}>
              {included.length > 0 ? (
                <View style={{ flex: 1 }}>
                  <Text style={s.sectionTitle}>Inclus</Text>
                  {included.map((item, i) => (
                    <View key={i} style={s.listItem}>
                      <Text style={s.bullet}>✓</Text>
                      <Text style={s.listText}>{clean(item)}</Text>
                    </View>
                  ))}
                </View>
              ) : null}
              {excluded.length > 0 ? (
                <View style={{ flex: 1 }}>
                  <Text style={s.sectionTitle}>Non inclus</Text>
                  {excluded.map((item, i) => (
                    <View key={i} style={s.listItem}>
                      <Text style={s.bulletRed}>✗</Text>
                      <Text style={s.listText}>{clean(item)}</Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          </View>
        ) : null}

        {/* Formules */}
        {pkg.formulas && pkg.formulas.length > 0 ? (
          <View style={{ ...s.section, ...s.pageBreak }}>
            <Text style={s.sectionTitle}>Formules disponibles</Text>
            {pkg.formulas.map((formula, fi) => {
              const hotels = formula.hotels ?? ((formula as any).hotel ? [(formula as any).hotel] : []);
              const discount = formula.discountPercent || 0;
              const applyDiscount = (v: number) => discount > 0 ? Math.round(v * (1 - discount / 100)) : v;

              return (
                <View key={fi} style={s.formulaCard} wrap={false}>
                  <Text style={s.formulaName}>{clean(formula.name)}</Text>

                  {/* Prix par personne */}
                  {formula.priceType === 'PER_PERSON' && formula.perPersonPrice && formula.perPersonPrice > 0 ? (
                    <View style={s.formulaRow}>
                      {discount > 0 ? <Text style={s.formulaPriceOld}>{fmtPrice(formula.perPersonPrice)} DA</Text> : null}
                      <Text style={s.formulaPrice}>{fmtPrice(applyDiscount(formula.perPersonPrice))} DA par personne</Text>
                    </View>
                  ) : null}

                  {/* Prix par type de chambre — uniquement ceux > 0 */}
                  {formula.priceType === 'PER_ROOM' && formula.roomPrices ? (() => {
                    const entries = Object.entries(formula.roomPrices).filter(([, v]) => v > 0);
                    if (entries.length === 0) return null;
                    return (
                      <View style={{ marginTop: 4, marginBottom: 4 }}>
                        <Text style={{ ...s.infoLabel, marginBottom: 4 }}>Prix par personne selon le type de chambre</Text>
                        {entries.map(([type, price]) => (
                          <View key={type} style={s.roomRow}>
                            <Text style={s.roomLabel}>{roomLabels[type] || type}</Text>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                              {discount > 0 ? <Text style={{ ...s.roomLabel, textDecoration: 'line-through', marginRight: 6 }}>{fmtPrice(price)} DA</Text> : null}
                              <Text style={s.roomPrice}>{fmtPrice(applyDiscount(price))} DA / pers.</Text>
                            </View>
                          </View>
                        ))}
                      </View>
                    );
                  })() : null}

                  {/* Services de la formule */}
                  {formula.services && formula.services.filter(sv => clean(sv)).length > 0 ? (
                    <View style={{ marginTop: 6 }}>
                      <Text style={s.infoLabel}>Services inclus</Text>
                      <View style={s.chipRow}>
                        {formula.services.filter(sv => clean(sv)).map((sv, si) => (
                          <View key={si} style={s.chip}><Text style={s.chipText}>{clean(sv)}</Text></View>
                        ))}
                      </View>
                    </View>
                  ) : null}

                  {/* Hôtels */}
                  {hotels.filter(h => clean(h.name)).map((hotel, hi) => (
                    <View key={hi} style={s.hotelBlock}>
                      <Text style={s.hotelName}>{clean(hotel.name)} {hotel.stars ? `${'★'.repeat(Number(hotel.stars))}` : ''}</Text>
                      {clean(hotel.address) ? <Text style={s.hotelDetail}>{clean(hotel.address)}</Text> : null}
                      {hotel.amenities && hotel.amenities.filter(a => clean(a)).length > 0 ? (
                        <View style={s.chipRow}>
                          {hotel.amenities.filter(a => clean(a)).map((a, ai) => (
                            <View key={ai} style={s.chip}><Text style={s.chipText}>{clean(a)}</Text></View>
                          ))}
                        </View>
                      ) : null}
                      {hotel.meals && hotel.meals.filter(m => clean(m)).length > 0 ? (
                        <View style={{ ...s.chipRow, marginTop: 2 }}>
                          {hotel.meals.filter(m => clean(m)).map((m, mi) => (
                            <View key={mi} style={s.mealChip}><Text style={s.mealText}>{clean(m)}</Text></View>
                          ))}
                        </View>
                      ) : null}
                    </View>
                  ))}
                </View>
              );
            })}
          </View>
        ) : null}

        {/* Itinéraire */}
        {hasItinerary ? (
          <View style={{ ...s.section, ...s.pageBreak }}>
            <Text style={s.sectionTitle}>Itinéraire</Text>
            {pkg.itinerary.map((day: DayItinerary) => {
              const activities = (day.activities || []).filter(a => clean(a));
              const meals = [day.meals?.breakfast && 'Petit-déjeuner', day.meals?.lunch && 'Déjeuner', day.meals?.dinner && 'Dîner'].filter(Boolean) as string[];
              return (
                <View key={day.id} style={s.dayCard} wrap={false}>
                  <Text style={s.dayTitle}>Jour {day.dayNumber} — {clean(day.title)}</Text>
                  {clean(day.description) ? <Text style={s.dayDesc}>{clean(day.description)}</Text> : null}
                  {activities.length > 0 ? activities.map((a, ai) => (
                    <View key={ai} style={s.listItem}><Text style={s.bullet}>•</Text><Text style={{ ...s.listText, fontSize: 9 }}>{clean(a)}</Text></View>
                  )) : null}
                  {meals.length > 0 ? (
                    <View style={s.mealRow}>
                      {meals.map((m, mi) => <View key={mi} style={s.mealChip}><Text style={s.mealText}>{m}</Text></View>)}
                    </View>
                  ) : null}
                  {clean(day.accommodation) ? <Text style={s.accomText}>Hébergement : {clean(day.accommodation)}</Text> : null}
                </View>
              );
            })}
          </View>
        ) : null}

        {/* Gallery */}
        {hasImages ? (
          <View style={{ ...s.section, ...s.pageBreak }}>
            <Text style={s.sectionTitle}>Galerie</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {pkg.images.map((img, i) => (
                <Image key={i} src={getImageUrl(img)} style={{ width: '48%', height: 130, objectFit: 'cover', borderRadius: 4 }} />
              ))}
            </View>
          </View>
        ) : null}

        {/* Footer */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>{clean(agency.name)} — {agency.phone}</Text>
          <Text style={s.footerText}>Généré le {new Date().toLocaleDateString('fr-FR')}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PackagePDF;
